/**
 * Date Formatter
 *
 * Utility class for formatting dates in English locale ("en-US")
 * Uses native Intl.DateTimeFormat for zero-dependency implementation
 */

export class DateFormatter {
  /**
   * Format date as full English date: "February 16, 2026"
   */
  static fullDate(date: Date | string): string {
    const d = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(d);
  }

  /**
   * Format date as short English date: "02/16/2026"
   */
  static shortDate(date: Date | string): string {
    const d = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  }

  /**
   * Format date as ISO 8601: "2026-02-16"
   */
  static isoDate(date: Date | string): string {
    const d = typeof date === "string" ? new Date(date) : date;
    const [datePart] = d.toISOString().split("T");
    return datePart ?? "";
  }

  /**
   * Format relative time in English: "2 days ago", "3 weeks ago"
   */
  static relativeTime(date: Date | string): string {
    const d = typeof date === "string" ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "today";
    } else if (diffDays === 1) {
      return "yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? "1 month ago" : `${months} months ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return years === 1 ? "1 year ago" : `${years} years ago`;
    }
  }

  /**
   * Format date with time: "February 16, 2026 2:30 PM"
   */
  static fullDateTime(date: Date | string): string {
    const d = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  }

  /**
   * Format month and year: "February 2026"
   */
  static monthYear(date: Date | string): string {
    const d = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
    }).format(d);
  }
}
